import { Box, Divider, Stack, Typography } from '@mui/material';
import { CloudAccountType, Provider } from '@repo/api-gw-sdk';
import { useState } from 'react';

import { useCloudFormationValidateOrganization } from '@/components/accounts/validateAccount';
import { ButtonWithIcon } from '@/components/buttons/buttonWithIcon';
import { getCloudFieldConfig } from '@/components/form/validation/cloudFieldValidation';
import { StepContainer } from '@/components/wizard/StepContainer';
import type { StepProps } from '@/components/wizard/StepProps';
import { useDAL } from '@/contexts/useDAL';

import { StepErrorDialog } from '../../../../../components/dialogs/stepErrorDialog';
import { FormTextField } from '../../../../../components/form/formTextField';
import { BulletItem } from '../bulletItem';
import { DiscoveryConfigurationAccordion } from '../discoveryConfigurationAccordion';
import { extractErrorMessage } from '../errorHandling';
import { NumericBulletSection } from '../numericBulletSection';

const ouIdConfig = getCloudFieldConfig(Provider.Aws, 'organizationUnitId');

const managementAccountIdConfig = getCloudFieldConfig(
  Provider.Aws,
  'accountId'
);

export interface AwsConnectOrganizationUnitState {
  onboardingFlow: boolean;
  organizationId: string;
  managementAccountId: string;
}

export const ConnectAwsSourceOrganizationUnit = (
  props: StepProps<AwsConnectOrganizationUnitState>
) => {
  const [openCloudFormationClicked, setOpenCloudFormationClicked] =
    useState(false);
  const [orgRoleName, setOrgRoleName] = useState(`EonOrganizationAccountRole`);
  const [isDeploying, setIsDeploying] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const { openCloudFormationForValidateOrganization: validateOrganization } =
    useCloudFormationValidateOrganization();

  const dal = useDAL();

  const isMissingFields =
    !props.wizardState.organizationId ||
    !props.wizardState.managementAccountId ||
    !orgRoleName;

  const canGoNext = () => {
    if (!openCloudFormationClicked) {
      return false;
    }
    const validationErrors =
      !ouIdConfig.validation.validateInput(props.wizardState.organizationId) ||
      !managementAccountIdConfig.validation.validateInput(
        props.wizardState.managementAccountId
      );

    return !isDeploying && !validationErrors && !isMissingFields;
  };

  const openCloudFormation = () => {
    // once clicked once, we are enabling the connection button.
    setOpenCloudFormationClicked(true);
    validateOrganization(
      orgRoleName,
      CloudAccountType.Source,
      props.wizardState.organizationId
    );
  };

  const connectOrganization = async () => {
    setError(null);
    setIsDeploying(true);

    try {
      await dal.cloudAccounts.sourceAwsOrganizationalUnit.connect({
        providerOrganizationalUnitId: props.wizardState.organizationId,
        roleArn: `arn:aws:iam::${props.wizardState.managementAccountId}:role/${orgRoleName}`,
      });
      await props.finish();
    } catch (error: unknown) {
      const errorMessage = await extractErrorMessage(error);
      setError(new Error(errorMessage));
    } finally {
      setIsDeploying(false);
    }
  };

  const nextButtonTooltip = isDeploying
    ? 'Please wait for the deployment to complete'
    : isMissingFields
      ? 'Please fill the missing fields'
      : !openCloudFormationClicked
        ? 'Please click the button to launch CloudFormation'
        : '';

  return (
    <>
      <StepContainer
        stepperIndex={3}
        toolbarComponent={
          <Typography variant='body1' paddingLeft={'40px'}>
            Before you start, make sure you can manage roles in your AWS
            environment.
          </Typography>
        }
        hideStepper
        stepperLabels={props.stepperLabels}
        canGoNext={canGoNext}
        nextButtonDisabledTooltip={nextButtonTooltip}
        nextButtonText='Connect'
        backButtonText='Cancel'
        onBackClick={props.back}
        sx={{ overflow: 'auto', paddingX: '0px', paddingY: 'min(60px, 4vw)' }}
        onNextClick={connectOrganization}
      >
        <Stack divider={<Divider />} gap='24px'>
          <Stack gap='min(60px, 4vw)' flexGrow={1} divider={<Divider />}>
            <Box paddingX='min(100px,6vw)' height='auto'>
              <NumericBulletSection
                bulletNumber={1}
                title='Set connection details'
              >
                <BulletItem
                  leftContent={
                    <Stack
                      gap='40px'
                      divider={<Divider sx={{ borderStyle: 'dashed' }} />}
                    >
                      <Stack gap='24px'>
                        <FormTextField
                          title='Organizational Unit ID'
                          placeholder={ouIdConfig.placeholder}
                          validation={ouIdConfig.validation}
                          value={props.wizardState.organizationId || ''}
                          onChange={(event) =>
                            props.setWizardState({
                              ...props.wizardState,
                              organizationId: event.target.value,
                            })
                          }
                        />
                        <FormTextField
                          title='Management account ID'
                          placeholder={managementAccountIdConfig.placeholder}
                          validation={managementAccountIdConfig.validation}
                          value={props.wizardState.managementAccountId || ''}
                          onChange={(event) =>
                            props.setWizardState({
                              ...props.wizardState,
                              managementAccountId: event.target.value,
                            })
                          }
                        />
                      </Stack>
                      <FormTextField
                        title='Eon role name'
                        placeholder='Enter role name'
                        value={orgRoleName}
                        onChange={(event) => setOrgRoleName(event.target.value)}
                      />
                    </Stack>
                  }
                />
              </NumericBulletSection>
            </Box>
            <Box paddingX='min(100px,6vw)' width='100%' height='auto'>
              <NumericBulletSection bulletNumber={2} title='Review and deploy'>
                <BulletItem
                  title='Launch CloudFormation to review and deploy Eon’s permissions in your AWS organization unit. Afterward, come back to this tab to finish the connection.'
                  leftContent={
                    <ButtonWithIcon
                      sx={{ width: '250px' }}
                      disabled={
                        !props.wizardState.organizationId ||
                        !props.wizardState.managementAccountId ||
                        !orgRoleName
                      }
                      variant='contained'
                      color='primary'
                      text='Launch CloudFormation in AWS'
                      icon='material-symbols-open-in-new'
                      iconPosition='right'
                      onClick={openCloudFormation}
                    />
                  }
                />
              </NumericBulletSection>
            </Box>
          </Stack>
          {props.wizardState.onboardingFlow && (
            <Stack gap='24px'>
              <DiscoveryConfigurationAccordion />
              <Divider />
            </Stack>
          )}
        </Stack>
      </StepContainer>
      <StepErrorDialog
        isOpen={!!error}
        onClose={() => {
          setError(null);
          setIsDeploying(false);
        }}
        error={error}
      />
    </>
  );
};
